import {
  Phone, Mail, MapPin, Clock, Shield, Lock, CheckCircle2, ArrowRight,
  MessageSquare, FileSearch, ClipboardCheck, HandshakeIcon, Star, Award, CalendarCheck,
} from "lucide-react";

import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { brand } from "@/data/site/brand";
import { Reveal } from "@/components/motion/Reveal";
import { SocialLinks } from "@/components/site/SocialLinks";
import { CaseReviewForm } from "@/components/site/CaseReviewForm";
import franklinPortrait from "@/assets/franklin-sofi.webp";

const Contact = () => {
  return (

    <SiteLayout>
      <PageSEO
        title="Contact a CPA — Free, Confidential IRS Case Review"
        description={`Talk to a credentialed CPA at ${brand.legalName}. Free, confidential case review — no sales pressure. Call ${brand.phone} or request a callback.`}
        path="/contact"
        image={stockImages.consultation.src}
      />

      {/* HERO */}
      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.consultation} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14 lg:py-24 max-w-6xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-4">
              Free · Confidential · No obligation
            </p>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance] max-w-4xl">
              Talk to a CPA.{" "}
              <span className="text-gold">No call center. No sales pitch.</span>
            </h1>
            <p className="text-lg text-white/85 max-w-2xl leading-relaxed mb-8">
              A licensed CPA — not a salesperson — reviews your facts and tells you what's
              realistic. If we can't help, we'll tell you who can. Most callers know their next
              step within 15 minutes.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={`tel:${brand.phoneTel}`}
                className="inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3 font-bold text-navy-dark shadow-md hover:bg-gold-light transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call {brand.phone}
              </a>
              <Link
                to="/schedule"
                className="inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 font-bold text-navy hover:bg-white/90 transition-colors"
              >
                <CalendarCheck className="w-5 h-5" />
                Schedule a free consultation
              </Link>
              <a
                href="#case-review-form"
                className="inline-flex items-center gap-2 rounded-md border-2 border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Request a callback <ArrowRight className="w-4 h-4" />
              </a>
            </div>

          </Reveal>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-secondary border-b border-border">
        <div className="container mx-auto px-4 py-6 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Shield, label: "CPA-led", sub: "Treasury Circular 230" },
              { icon: Lock, label: "Confidential", sub: "IRS §7216 protected" },
              { icon: Award, label: "15+ years", sub: "IRS representation" },
              { icon: Star, label: "5.0 rating", sub: "Verified clients" },
            ].map((t) => (
              <div key={t.label} className="flex flex-col items-center">
                <t.icon className="w-6 h-6 text-gold mb-2" />
                <div className="font-bold text-navy-dark text-sm">{t.label}</div>
                <div className="text-xs text-muted-foreground">{t.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN: FORM + SIDE */}
      <section className="py-14 lg:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-6xl grid lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14">
          {/* FORM */}
          <Reveal id="case-review-form">
            <div className="rounded-lg border border-border bg-gradient-navy text-white p-5 lg:p-8 shadow-lg">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
                Request your free case review
              </p>
              <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-2 leading-tight">
                Tell us what's going on.
              </h2>
              <p className="text-white/75 text-sm mb-5 leading-relaxed">
                Takes 60 seconds. A CPA reviews your details and calls you back —
                usually same business day.
              </p>
              <div className="rounded">
                <CaseReviewForm language="en" tone="dark" />
              </div>

              <div className="mt-5 flex items-start gap-2 text-xs text-white/70">
                <Lock className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                <p>
                  Submissions are confidential under IRS §7216 and Treasury Circular 230.
                  We never sell, share, or rent your information.
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-lg border-l-4 border-gold bg-secondary p-5">
              <p className="text-sm text-foreground leading-relaxed">
                <strong className="text-navy-dark">Time-sensitive?</strong>{" "}
                If you've received a CP504, Notice of Intent to Levy, or a wage garnishment,
                please call{" "}
                <a href={`tel:${brand.phoneTel}`} className="font-bold text-navy underline">
                  {brand.phone}
                </a>{" "}
                instead — a CPA picks up.
              </p>
            </div>
          </Reveal>

          {/* SIDE: CPA + Contact + Methods */}
          <Reveal direction="right" className="space-y-6">
            {/* Who you'll talk to */}
            <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-4">
                Who you'll talk to
              </p>
              <div className="flex gap-4 items-start">
                <img
                  src={franklinPortrait}
                  alt="Franklin Sofi, MBA, CPA — Founder of Comprehensive Tax Resolution"
                  className="w-20 h-20 rounded-full object-cover border-2 border-gold flex-shrink-0"
                  width={80}
                  height={80}
                  loading="lazy"
                />
                <div>
                  <div className="font-heading font-bold text-lg text-navy-dark leading-tight">
                    Franklin Sofi, MBA, CPA
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">Founder · 15+ yrs IRS rep</div>
                  <p className="text-sm text-foreground leading-relaxed italic">
                    "I answer every call myself. No call center, no junior intake rep."
                  </p>
                </div>
              </div>
            </div>

            {/* Contact methods */}
            <div className="rounded-lg border border-border bg-white p-6 shadow-sm space-y-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Direct contact
              </p>
              <InfoRow icon={Phone} label="Phone" value={brand.phone} href={`tel:${brand.phoneTel}`} sub="Avg. answer time: 30s" />
              <InfoRow icon={Mail} label="Email" value={brand.email} href={`mailto:${brand.email}`} sub="Replies within 1 business hour" />
              <InfoRow icon={MapPin} label="Office" value={brand.address.full} sub="In-person by appointment" />
              <InfoRow icon={Clock} label="Hours" value={brand.hours} sub="Closed weekends & federal holidays" />
            </div>

            {/* Follow */}
            <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                Follow us
              </p>
              <SocialLinks variant="contact" />
            </div>

            {/* Spanish */}
            <Link
              to="/ayuda"
              className="block rounded-lg bg-gradient-navy text-white p-6 shadow-md hover:shadow-lg transition-shadow group"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
                ¿Habla español?
              </p>
              <div className="font-heading font-bold text-xl mb-1">Ayuda con el IRS</div>
              <p className="text-white/80 text-sm mb-3">
                Consulta gratis y confidencial con un CPA. Hablamos español.
              </p>
              <span className="inline-flex items-center gap-2 text-gold font-semibold text-sm group-hover:gap-3 transition-all">
                Ir a la página en español <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="py-16 lg:py-24 bg-secondary">
        <div className="container mx-auto px-4 max-w-6xl">
          <Reveal className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-3">
              What happens after you reach out
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-dark leading-tight">
              From first call to IRS resolution — no surprises.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MessageSquare, n: "01", title: "We talk — free & confidential", body: "15-minute call with a CPA. You explain what's happening; we tell you what the IRS can and can't do, and what your real options are." },
              { icon: FileSearch, n: "02", title: "We pull your IRS file", body: "With a signed 2848 we pull your full IRS transcript — every notice, balance, lien, levy. You see exactly what we see." },
              { icon: ClipboardCheck, n: "03", title: "Flat-fee resolution plan", body: "Written engagement letter with scope, milestones, and a fixed fee. No hourly billing, no surprise invoices." },
              { icon: HandshakeIcon, n: "04", title: "We handle the IRS", body: "You stop talking to the IRS. We file, negotiate, and resolve — and keep you informed every step." },
            ].map((s) => (
              <div key={s.n} className="rounded-lg border border-border bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="rounded-lg bg-secondary p-2.5 group-hover:bg-gold transition-colors">
                    <s.icon className="w-6 h-6 text-navy-dark" />
                  </div>
                  <span className="text-3xl font-heading font-bold text-gold/40">{s.n}</span>
                </div>
                <h3 className="font-heading font-bold text-lg text-navy-dark mb-2 leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALL VS FORM */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <Reveal className="text-center mb-10">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-dark mb-3">
              Should you call or fill out the form?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Both reach the same CPA. Here's how to decide.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-lg border-2 border-gold bg-white p-7 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-lg bg-gold p-2.5">
                  <Phone className="w-6 h-6 text-navy-dark" />
                </div>
                <h3 className="font-heading font-bold text-xl text-navy-dark">Call us</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Recommended when the IRS is actively moving against you.
              </p>
              <ul className="space-y-2">
                {[
                  "Wage garnishment or bank levy in motion",
                  "Final Notice / CP90 / LT11 received",
                  "Audit appointment in the next 14 days",
                  "You just want a real person, fast",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`tel:${brand.phoneTel}`}
                className="mt-5 inline-flex items-center gap-2 font-bold text-navy hover:text-gold transition-colors"
              >
                {brand.phone} <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="rounded-lg border border-border bg-white p-7 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-lg bg-secondary p-2.5">
                  <Mail className="w-6 h-6 text-navy-dark" />
                </div>
                <h3 className="font-heading font-bold text-xl text-navy-dark">Use the form</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Best when you'd rather we call you back at a specific time.
              </p>
              <ul className="space-y-2">
                {[
                  "You're at work and can't talk now",
                  "You want time to gather your notices first",
                  "It's after hours or the weekend",
                  "You prefer to write out the backstory",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#case-review-form"
                className="mt-5 inline-flex items-center gap-2 font-bold text-navy hover:text-gold transition-colors"
              >
                Jump to the form <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-secondary">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-3">
              Before you call
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-dark">
              Quick answers
            </h2>
          </Reveal>
          <div className="space-y-3">
            {[
              { q: "Is the consultation really free?", a: "Yes. A 15-minute case review with a CPA is free, confidential, and no-obligation. We don't ask for payment info, and we don't pressure you onto a call. If we engage, fees are flat and disclosed in writing first." },
              { q: "Who actually answers the phone?", a: "Franklin Sofi, MBA, CPA, or another credentialed team member — never a call center or sales rep. If everyone is on another call, leave a voicemail and you'll get a callback the same business day." },
              { q: "Will calling you make the IRS notice me?", a: "No. We don't contact the IRS until you sign a Form 2848 (Power of Attorney) authorizing us. Until then, our conversation is entirely between us and protected under IRS §7216." },
              { q: "How fast can you stop a garnishment or levy?", a: "Often within 24–72 hours of being engaged, depending on the IRS office and how much information you can provide upfront. The sooner you call, the more options you have." },
              { q: "Do you handle state tax problems too?", a: "Yes — we represent clients before state taxing authorities (FTB, Comptroller, DOR, etc.) as well as the IRS. Federal and state often need to be resolved together." },
              { q: "I don't live near Prosper, TX — does that matter?", a: "No. We represent clients nationwide. Everything is handled by phone, email, and secure document portal. We've never met most of our clients in person." },
            ].map((f) => (
              <details key={f.q} className="group rounded-lg border border-border bg-white p-5 shadow-sm">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-semibold text-navy-dark">
                  <span>{f.q}</span>
                  <span className="text-gold text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 lg:py-20 bg-gradient-navy text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Reveal>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold mb-5 leading-tight [text-wrap:balance]">
              The IRS won't wait. <span className="text-gold">Neither should you.</span>
            </h2>
            <p className="text-lg text-white/85 mb-8 max-w-2xl mx-auto">
              Penalties and interest accrue daily. One honest conversation with a CPA can change
              the trajectory of your case.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href={`tel:${brand.phoneTel}`}
                className="inline-flex items-center gap-2 rounded-md bg-gold px-7 py-3.5 font-bold text-navy-dark shadow-md hover:bg-gold-light transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call {brand.phone}
              </a>
              <a
                href="#case-review-form"
                className="inline-flex items-center gap-2 rounded-md border-2 border-white/30 px-7 py-3.5 font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Request a callback <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
};

const InfoRow = ({
  icon: Icon, label, value, href, sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
  sub?: string;
}) => (
  <div className="flex items-start gap-3">
    <div className="rounded-md bg-secondary p-2 flex-shrink-0">
      <Icon className="w-4 h-4 text-navy-dark" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-0.5">
        {label}
      </div>
      {href ? (
        <a href={href} className="font-semibold text-navy-dark hover:text-gold break-words text-sm">
          {value}
        </a>
      ) : (
        <div className="font-semibold text-navy-dark text-sm">{value}</div>
      )}
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  </div>
);

export default Contact;
